import type {
  HistogramBucket,
  DistributionItem,
  PriceMode,
} from './DistributionSection.types'

/**
 * Process room histogram buckets into distribution items
 */
export const processRoomsBuckets = (
  buckets: HistogramBucket[]
): DistributionItem[] => {
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return []

  // Group rooms into categories
  const groupedCounts: Record<string, { value: number; count: number }> = {}
  const categories = [
    { label: '≤1.5', max: 1.5 },
    { label: '2-2.5', min: 2, max: 2.5 },
    { label: '3-3.5', min: 3, max: 3.5 },
    { label: '4-4.5', min: 4, max: 4.5 },
    { label: '5-5.5', min: 5, max: 5.5 },
    { label: '6-6.5', min: 6, max: 6.5 },
    { label: '7+', min: 7 },
  ]

  categories.forEach((cat) => {
    groupedCounts[cat.label] = { value: cat.min || 0, count: 0 }
  })

  buckets.forEach((bucket) => {
    const rooms = bucket.key
    let category = '7+'
    if (rooms <= 1.5) category = '≤1.5'
    else if (rooms <= 2.5) category = '2-2.5'
    else if (rooms <= 3.5) category = '3-3.5'
    else if (rooms <= 4.5) category = '4-4.5'
    else if (rooms <= 5.5) category = '5-5.5'
    else if (rooms <= 6.5) category = '6-6.5'

    groupedCounts[category].count += bucket.doc_count
  })

  return categories
    .filter((cat) => groupedCounts[cat.label].count > 0)
    .map((cat) => ({
      label: cat.label,
      value: cat.min || 0,
      count: groupedCounts[cat.label].count,
      percentage: (groupedCounts[cat.label].count / total) * 100,
    }))
}

/**
 * Format a price value into a compact label (e.g., 6000 -> "6k", 18000 -> "18k")
 */
const compactPriceLabel = (value: number): string => {
  if (value >= 1000000)
    return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`
  if (value >= 1000)
    return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`
  return `${value}`
}

/**
 * Process price histogram buckets into distribution items with grouped categories
 */
export const processPriceBuckets = (
  buckets: HistogramBucket[],
  isRental: boolean,
  priceMode?: PriceMode
): DistributionItem[] => {
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return []

  const multiplier = isRental && priceMode === 'yearly' ? 12 : 1

  // Define price categories with thresholds (always monthly for grouping)
  const categories = isRental
    ? [
        { label: `<${compactPriceLabel(500 * multiplier)}`, max: 500 },
        {
          label: `${compactPriceLabel(500 * multiplier)}-${compactPriceLabel(1000 * multiplier)}`,
          min: 500,
          max: 1000,
        },
        {
          label: `${compactPriceLabel(1000 * multiplier)}-${compactPriceLabel(1500 * multiplier)}`,
          min: 1000,
          max: 1500,
        },
        {
          label: `${compactPriceLabel(1500 * multiplier)}-${compactPriceLabel(2000 * multiplier)}`,
          min: 1500,
          max: 2000,
        },
        {
          label: `${compactPriceLabel(2000 * multiplier)}-${compactPriceLabel(2500 * multiplier)}`,
          min: 2000,
          max: 2500,
        },
        {
          label: `${compactPriceLabel(2500 * multiplier)}-${compactPriceLabel(3000 * multiplier)}`,
          min: 2500,
          max: 3000,
        },
        {
          label: `${compactPriceLabel(3000 * multiplier)}-${compactPriceLabel(4000 * multiplier)}`,
          min: 3000,
          max: 4000,
        },
        { label: `>${compactPriceLabel(4000 * multiplier)}`, min: 4000 },
      ]
    : [
        { label: '<200k', max: 200000 },
        { label: '200k-400k', min: 200000, max: 400000 },
        { label: '400k-600k', min: 400000, max: 600000 },
        { label: '600k-800k', min: 600000, max: 800000 },
        { label: '800k-1M', min: 800000, max: 1000000 },
        { label: '1M-1.5M', min: 1000000, max: 1500000 },
        { label: '1.5M-2M', min: 1500000, max: 2000000 },
        { label: '>2M', min: 2000000 },
      ]

  // Initialize counts for each category
  const groupedCounts: Record<string, number> = {}
  categories.forEach((cat) => {
    groupedCounts[cat.label] = 0
  })

  // Group buckets into categories
  buckets.forEach((bucket) => {
    const price = bucket.key
    for (const cat of categories) {
      const minVal = cat.min ?? 0
      const maxVal = cat.max ?? Infinity
      if (price >= minVal && price < maxVal) {
        groupedCounts[cat.label] += bucket.doc_count
        break
      }
    }
  })

  return categories
    .filter((cat) => groupedCounts[cat.label] > 0)
    .map((cat) => ({
      label: cat.label,
      value: cat.min ?? 0,
      count: groupedCounts[cat.label],
      percentage: (groupedCounts[cat.label] / total) * 100,
    }))
}

/**
 * Process price/m² histogram buckets into distribution items with grouped categories
 */
export const processPriceM2Buckets = (
  buckets: HistogramBucket[],
  isRental: boolean,
  priceMode?: PriceMode
): DistributionItem[] => {
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return []

  const multiplier = isRental && priceMode === 'yearly' ? 12 : 1

  // Define price/m² categories with thresholds (always monthly for grouping)
  const categories = isRental
    ? [
        { label: `<${compactPriceLabel(20 * multiplier)}`, max: 20 },
        {
          label: `${compactPriceLabel(20 * multiplier)}-${compactPriceLabel(30 * multiplier)}`,
          min: 20,
          max: 30,
        },
        {
          label: `${compactPriceLabel(30 * multiplier)}-${compactPriceLabel(40 * multiplier)}`,
          min: 30,
          max: 40,
        },
        {
          label: `${compactPriceLabel(40 * multiplier)}-${compactPriceLabel(50 * multiplier)}`,
          min: 40,
          max: 50,
        },
        {
          label: `${compactPriceLabel(50 * multiplier)}-${compactPriceLabel(60 * multiplier)}`,
          min: 50,
          max: 60,
        },
        {
          label: `${compactPriceLabel(60 * multiplier)}-${compactPriceLabel(80 * multiplier)}`,
          min: 60,
          max: 80,
        },
        { label: `>${compactPriceLabel(80 * multiplier)}`, min: 80 },
      ]
    : [
        { label: '<4k', max: 4000 },
        { label: '4k-6k', min: 4000, max: 6000 },
        { label: '6k-8k', min: 6000, max: 8000 },
        { label: '8k-10k', min: 8000, max: 10000 },
        { label: '10k-12k', min: 10000, max: 12000 },
        { label: '12k-15k', min: 12000, max: 15000 },
        { label: '>15k', min: 15000 },
      ]

  // Initialize counts for each category
  const groupedCounts: Record<string, number> = {}
  categories.forEach((cat) => {
    groupedCounts[cat.label] = 0
  })

  // Group buckets into categories
  buckets.forEach((bucket) => {
    const priceM2 = bucket.key
    for (const cat of categories) {
      const minVal = cat.min ?? 0
      const maxVal = cat.max ?? Infinity
      if (priceM2 >= minVal && priceM2 < maxVal) {
        groupedCounts[cat.label] += bucket.doc_count
        break
      }
    }
  })

  return categories
    .filter((cat) => groupedCounts[cat.label] > 0)
    .map((cat) => ({
      label: cat.label,
      value: cat.min ?? 0,
      count: groupedCounts[cat.label],
      percentage: (groupedCounts[cat.label] / total) * 100,
    }))
}
